import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../../api/axiosInstance';

const BASE = '/dashboard_data/project';

export function errorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (error?.response?.status === 403) return 'You do not have access to this project.';
  return 'The data could not be loaded.';
}

/**
 * Loads one dashboard section. Each section fails and retries independently;
 * responses for superseded params are dropped so a slow request never overwrites newer data.
 * `pollMs` (when set) refreshes silently in the background.
 */
export default function useDashboardSection(endpoint, params, { enabled = true, pollMs = null, base = BASE } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: enabled, refreshing: false });
  const requestRef = useRef(0);
  const controllerRef = useRef(null);
  const paramsKey = JSON.stringify(params || {});

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled) return;
      requestRef.current += 1;
      const requestId = requestRef.current;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setState((prev) =>
        silent ? { ...prev, refreshing: true } : { ...prev, loading: prev.data == null, refreshing: prev.data != null, error: null }
      );
      try {
        const response = await axiosInstance.get(`${base}/${endpoint}`, {
          params: JSON.parse(paramsKey),
          signal: controller.signal,
          skipGlobalLoader: true,
          skipGlobalToast: true,
        });
        if (requestId !== requestRef.current) return;
        setState({ data: response?.data ?? null, error: null, loading: false, refreshing: false });
      } catch (error) {
        if (requestId !== requestRef.current || controller.signal.aborted) return;
        setState((prev) => ({
          data: silent ? prev.data : null,
          error: silent ? prev.error : errorMessage(error),
          loading: false,
          refreshing: false,
        }));
      }
    },
    [base, endpoint, paramsKey, enabled]
  );

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      load({ silent: true });
    }, pollMs);
    return () => clearInterval(timer);
  }, [pollMs, enabled, load]);

  const retry = useCallback(() => load(), [load]);
  return { ...state, retry, reload: load };
}
